// @flow
import * as React from 'react';
import { withRouter } from 'react-router';
import type { Location, RouterHistory } from 'react-router';
import { connect } from 'react-redux';
import { truncateToUTF8Bytes } from 'utils/string';
import SearchInputs from 'components/SearchInputs';
import type { CurrentUser } from 'api/currentUser';
import type { SearchSection } from 'api/shared';
import type { DisplayedType } from 'utils/constants';
import { listingTypesBySection } from 'utils/constants';
import { isEmpty, filterObject, mapObject, values } from 'utils/functional';
import {
  updateSearchLocation,
  clearSearchLocation,
  setSearchFilter,
  setSearchQuery
} from '../redux/staff/actions/search';

type OwnProps = {
  onSearch?: Object => void,
  searchSection: SearchSection,
  inPageHeader?: boolean,
  location: Location,
  history: RouterHistory
};

type Props = {
  ...$Exact<OwnProps>,
  type: ?StaffTypeFilter,
  query: string,
  searchLocation: ?SearchLocation,
  setSearchQuery: typeof setSearchQuery,
  setSearchFilter: typeof setSearchFilter,
  updateSearchLocation: typeof updateSearchLocation,
  clearSearchLocation: typeof clearSearchLocation,
  currentUser: CurrentUser
};

type State = {
  type: ?StaffTypeFilter,
  query: string,
  searchLocation: ?SearchLocation,
  prevProps: {
    type: ?StaffTypeFilter,
    query: string,
    searchLocation: ?SearchLocation
  }
};

// @TODO - move elsewhere
const toQueryParamKey = (k: string) => {
  const mapping = { query: 'q' };
  return mapping[k] || k;
};

const makeQueryString = (props: { type?: string, query?: string }) => {
  const search = values(
    mapObject(filterObject(props, k => !isEmpty(k)), (k, v) => [
      k,
      `${toQueryParamKey(k)}=${v}`
    ])
  ).join('&');
  return isEmpty(search) ? '' : `?${search}`;
};

class StaffSearchInputsContainer extends React.Component<Props, State> {
  state = {
    type: this.props.type || 'ALL',
    query: this.props.query || '',
    searchLocation: this.props.searchLocation,
    // eslint-disable-next-line react/no-unused-state
    prevProps: {
      type: this.props.type,
      query: this.props.query,
      searchLocation: this.props.searchLocation
    }
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    const updatedState = ['type', 'query', 'searchLocation'].reduce(
      (acc, prop) => {
        if (props[prop] !== state.prevProps[prop]) {
          acc[prop] = props[prop];
        }
        return acc;
      },
      {}
    );

    if (Object.keys(updatedState).length > 0) {
      updatedState.prevProps = {
        type: props.type,
        query: props.query,
        searchLocation: props.searchLocation
      };
      return updatedState;
    }
    return null;
  }

  setListingType = (listingType: ?StaffTypeFilter) => {
    this.setState({ type: listingType });
  };

  setQuery = (query: string, callback?: Function) => {
    // Algolia has a max query length of 512 bytes
    this.setState({ query: truncateToUTF8Bytes(query, 512) }, callback);
  };

  setSearchLocation = (searchLocation: ?SearchLocation) => {
    this.setState({ searchLocation }, () => this.search(this.state.query));
  };

  search = (query: string) => {
    if (this.props.type !== this.state.type) {
      this.props.setSearchFilter('type', this.state.type);
    }
    if (this.props.query !== query) {
      this.props.setSearchQuery(query);
    }
    if (this.props.searchLocation !== this.state.searchLocation) {
      const searchLocation = this.state.searchLocation;
      if (searchLocation) {
        this.props.updateSearchLocation(searchLocation);
      } else {
        this.props.clearSearchLocation();
      }
    }
    const queryString = makeQueryString({
      query,
      type: this.state.type || ''
    });
    this.props.history.push(`/staff${queryString}`);
  };

  render() {
    const { type, query, searchLocation } = this.state;
    const { setListingType, setQuery, setSearchLocation, search, props } = this;
    const listingTypes: Array<DisplayedType> = listingTypesBySection.STAFF;
    const selectedType =
      listingTypes.find(l => l === type) ||
      (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);
    const clearSearchInput = () => {
      setQuery('', () => search(''));
    };
    return (
      <SearchInputs
        listingTypes={listingTypes}
        selectedType={selectedType}
        onSearch={props.onSearch}
        query={query}
        clearSearchInput={clearSearchInput}
        searchLocation={searchLocation}
        setListingType={setListingType}
        setQuery={setQuery}
        setSearchLocation={setSearchLocation}
        search={search}
        inPageHeader={props.inPageHeader}
        currentUserLoaded={props.currentUser.isLoaded}
      />
    );
  }
}

const mapStateToProps = ({
  search: {
    query,
    filters: { type },
    searchLocation
  },
  currentUser
}) => ({ query, type, searchLocation, currentUser });

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // $FlowFixMe: react-redux complains about this type when wrapped in withRouter
    {
      setSearchFilter,
      setSearchQuery,
      updateSearchLocation,
      clearSearchLocation
    }
  )(StaffSearchInputsContainer)
);
